import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Editor } from '@tiptap/react';
import {
    Bold as BoldIcon,
    Italic as ItalicIcon,
    Underline as UnderlineIcon,
    Strikethrough as StrikethroughIcon,
    List as ListIcon,
    ListOrdered as ListOrderedIcon,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    ImagePlus,
    AlignHorizontalDistributeStart,
    AlignHorizontalDistributeCenter,
    AlignHorizontalDistributeEnd,
    Undo as UndoIcon,
    Redo as RedoIcon,
    RotateCcw,
    MoreHorizontal,
    ChevronDown
} from 'lucide-react';
import '../pages/Editor.css';

interface EditorToolbarProps {
    editor: Editor | null;
    isReadOnly?: boolean;
    onImageUpload: () => void;
    onUndoImprovement: () => void;
    hasImprovementToUndo: boolean;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
    editor,
    isReadOnly,
    onImageUpload,
    onUndoImprovement,
    hasImprovementToUndo
}) => {
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [regularVisibleCount, setRegularVisibleCount] = useState(0); // Start with 0 regular items
    const containerRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
    const [availableWidth, setAvailableWidth] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const [currentFontFamily, setCurrentFontFamily] = useState('');
    const [currentFontSize, setCurrentFontSize] = useState('');

    // Measure container width - must be before any early returns
    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setAvailableWidth(entry.contentRect.width);
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Calculate visible items - must be before any early returns
    // Priority items are always shown, regular items fill remaining space
    useLayoutEffect(() => {
        if (!availableWidth) return;

        const items = itemsRef.current.filter(item => item !== null);
        if (items.length === 0) return;

        const moreBtnWidth = 50; // Width of "More" button + margin
        const gap = 4; // Gap between items
        const separatorWidth = 17; // Width of separator between groups

        // Calculate width of priority items (indices 0 = textStyle, 3 = image)
        // Priority items are: textStyle (index 0) and image (index 3)
        const priorityIndices = [0, 3]; // textStyle and image
        let priorityWidth = 0;
        priorityIndices.forEach(idx => {
            const item = items[idx];
            if (item) {
                priorityWidth += item.offsetWidth + gap;
            }
        });
        // Add separator after textStyle group
        priorityWidth += separatorWidth;

        // Calculate remaining width for regular items
        const remainingWidth = availableWidth - priorityWidth - moreBtnWidth - gap;

        // Count how many regular items (non-priority) fit
        let regularWidth = 0;
        let regularCount = 0;
        let lastGroup = '';

        for (let i = 0; i < items.length; i++) {
            // Skip priority items in this count
            if (priorityIndices.includes(i)) continue;

            const item = items[i];
            if (item) {
                let itemWidth = item.offsetWidth + gap;

                // Check if we need a separator (simplified - just add some padding)
                if (regularCount > 0) {
                    itemWidth += 2; // Small extra for potential separator
                }

                if (regularWidth + itemWidth <= remainingWidth) {
                    regularWidth += itemWidth;
                    regularCount++;
                } else {
                    break;
                }
            }
        }

        // Show one less item to ensure More button is more visible/accessible
        setRegularVisibleCount(Math.max(0, regularCount - 1));
        setIsInitialized(true);
    }, [availableWidth]);

    // Track current font family and size from editor selection
    useEffect(() => {
        if (!editor) return;

        const updateFontState = () => {
            const attrs = editor.getAttributes('textStyle');
            setCurrentFontFamily(attrs.fontFamily || '');
            setCurrentFontSize(attrs.fontSize || '');
        };

        // Update on selection change
        editor.on('selectionUpdate', updateFontState);
        editor.on('transaction', updateFontState);

        // Initial update
        updateFontState();

        return () => {
            editor.off('selectionUpdate', updateFontState);
            editor.off('transaction', updateFontState);
        };
    }, [editor]);

    // Helper components
    const ToolbarButton = ({
        onClick,
        isActive = false,
        disabled = false,
        title,
        children
    }: {
        onClick: () => void;
        isActive?: boolean;
        disabled?: boolean;
        title: string;
        children: React.ReactNode;
    }) => (
        <button
            onClick={onClick}
            className={`toolbar-btn ${isActive ? 'active' : ''}`}
            title={title}
            disabled={disabled || isReadOnly}
        >
            {children}
        </button>
    );

    const ToolbarSeparator = () => <div className="toolbar-separator" />;

    if (!editor) return null;

    // Define individual items - priority items are always visible
    const allItems = [
        // Text Style Group - PRIORITY
        {
            id: 'textStyle',
            group: 'Text Style',
            priority: true,
            component: (
                <div className="toolbar-group">
                    <select
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'paragraph') {
                                editor.chain().focus().setParagraph().run();
                            } else if (value.startsWith('heading-')) {
                                const level = parseInt(value.replace('heading-', '')) as 1 | 2 | 3;
                                editor.chain().focus().toggleHeading({ level }).run();
                            }
                        }}
                        className="toolbar-select"
                        title="Text Style"
                        disabled={isReadOnly}
                        value={
                            editor.isActive('heading', { level: 1 }) ? 'heading-1' :
                                editor.isActive('heading', { level: 2 }) ? 'heading-2' :
                                    editor.isActive('heading', { level: 3 }) ? 'heading-3' :
                                        'paragraph'
                        }
                    >
                        <option value="paragraph">Normal</option>
                        <option value="heading-1">H1</option>
                        <option value="heading-2">H2</option>
                        <option value="heading-3">H3</option>
                    </select>
                </div>
            )
        },
        // Font Group
        {
            id: 'fontFamily',
            group: 'Font',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <select
                        value={currentFontFamily}
                        onChange={(e) => {
                            if (e.target.value) {
                                editor.chain().focus().setFontFamily(e.target.value).run();
                            } else {
                                editor.chain().focus().unsetFontFamily().run();
                            }
                        }}
                        className="toolbar-select"
                        title="Font Family"
                        disabled={isReadOnly}
                        style={{ width: '100px' }}
                    >
                        <option value="">Georgia</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Times New Roman, serif">Times</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                    </select>
                </div>
            )
        },
        {
            id: 'fontSize',
            group: 'Font',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <select
                        value={currentFontSize}
                        onChange={(e) => {
                            if (e.target.value) {
                                editor.chain().focus().setFontSize(e.target.value).run();
                            } else {
                                editor.chain().focus().unsetFontSize().run();
                            }
                        }}
                        className="toolbar-select font-size-select"
                        title="Font Size"
                        disabled={isReadOnly}
                    >
                        <option value="">20</option>
                        <option value="12px">12</option>
                        <option value="14px">14</option>
                        <option value="16px">16</option>
                        <option value="18px">18</option>
                        <option value="22px">22</option>
                        <option value="24px">24</option>
                        <option value="28px">28</option>
                        <option value="32px">32</option>
                        <option value="36px">36</option>
                    </select>
                </div>
            )
        },
        // Image Group - PRIORITY
        {
            id: 'image',
            group: 'Image',
            priority: true,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={onImageUpload} title="Insert Image">
                        <ImagePlus size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // Formatting Group
        {
            id: 'bold',
            group: 'Formatting',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
                        <BoldIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'italic',
            group: 'Formatting',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
                        <ItalicIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'underline',
            group: 'Formatting',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
                        <UnderlineIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'strike',
            group: 'Formatting',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strike">
                        <StrikethroughIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // Lists Group
        {
            id: 'bulletList',
            group: 'Lists',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
                        <ListIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'orderedList',
            group: 'Lists',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
                        <ListOrderedIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // Alignment Group
        {
            id: 'alignLeft',
            group: 'Alignment',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
                        <AlignLeft size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'alignCenter',
            group: 'Alignment',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
                        <AlignCenter size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'alignRight',
            group: 'Alignment',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
                        <AlignRight size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'alignJustify',
            group: 'Alignment',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify">
                        <AlignJustify size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // Image Alignment Group
        {
            id: 'imgLeft',
            group: 'Image Align',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setImageFloat('left').run()} isActive={editor.isActive('resizableImage', { float: 'left' })} title="Float Left">
                        <AlignHorizontalDistributeStart size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'imgCenter',
            group: 'Image Align',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setImageFloat('none').run()} isActive={editor.isActive('resizableImage', { float: 'none' })} title="Center">
                        <AlignHorizontalDistributeCenter size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'imgRight',
            group: 'Image Align',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().setImageFloat('right').run()} isActive={editor.isActive('resizableImage', { float: 'right' })} title="Float Right">
                        <AlignHorizontalDistributeEnd size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // History Group
        {
            id: 'undo',
            group: 'History',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
                        <UndoIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        {
            id: 'redo',
            group: 'History',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
                        <RedoIcon size={18} />
                    </ToolbarButton>
                </div>
            )
        },
        // AI Group
        {
            id: 'aiUndo',
            group: 'AI',
            priority: false,
            component: (
                <div className="toolbar-group">
                    <ToolbarButton onClick={onUndoImprovement} disabled={!hasImprovementToUndo} title="Undo AI">
                        <RotateCcw size={18} />
                    </ToolbarButton>
                </div>
            )
        }
    ];

    // Split items into priority and regular
    const priorityItems = allItems.filter(item => item.priority);
    const regularItems = allItems.filter(item => !item.priority);

    // Visible regular items based on available space
    const visibleRegularItems = regularItems.slice(0, regularVisibleCount);
    const overflowItems = regularItems.slice(regularVisibleCount);

    // Group overflow items for the dropdown
    const overflowGroups: { [key: string]: React.ReactNode[] } = {};
    overflowItems.forEach(item => {
        if (!overflowGroups[item.group]) {
            overflowGroups[item.group] = [];
        }
        overflowGroups[item.group].push(item.component);
    });

    // Check if we should show the more button
    const hasOverflow = overflowItems.length > 0;

    return (
        <div ref={containerRef} className={`editor-toolbar ${isReadOnly ? 'disabled' : ''} ${!isInitialized ? 'initializing' : ''}`}>
            {/*
                Hidden measurement container:
                Render all items invisibly to measure their natural width.
                Uses left: -9999px to prevent affecting layout.
            */}
            <div style={{
                position: 'absolute',
                left: '-9999px',
                top: 0,
                display: 'flex',
                gap: '4px',
                visibility: 'hidden',
                pointerEvents: 'none'
            }}>
                {allItems.map((item, index) => (
                    <div key={item.id} ref={el => { itemsRef.current[index] = el; }}>
                        {item.component}
                    </div>
                ))}
            </div>

            {/* Priority Items (always visible) */}
            {priorityItems.map((item, index) => (
                <React.Fragment key={item.id}>
                    {index > 0 && item.group !== priorityItems[index - 1].group && <ToolbarSeparator />}
                    {item.component}
                </React.Fragment>
            ))}

            {/* Separator between priority and regular items */}
            {visibleRegularItems.length > 0 && <ToolbarSeparator />}

            {/* Regular Visible Items */}
            {visibleRegularItems.map((item, index) => (
                <React.Fragment key={item.id}>
                    {index > 0 && item.group !== visibleRegularItems[index - 1].group && <ToolbarSeparator />}
                    {item.component}
                </React.Fragment>
            ))}

            {/* Overflow Menu - always show if there are overflow items */}
            {hasOverflow && (
                <div className="toolbar-more-section">
                    <button
                        className={`toolbar-btn more-btn ${showMoreMenu ? 'active' : ''}`}
                        onClick={() => setShowMoreMenu(!showMoreMenu)}
                        title="More Options"
                    >
                        <MoreHorizontal size={18} />
                    </button>

                    {showMoreMenu && (
                        <div className="toolbar-dropdown">
                            {Object.entries(overflowGroups).map(([groupName, components]) => (
                                <div key={groupName} className="toolbar-dropdown-group">
                                    <div className="toolbar-group-label">{groupName}</div>
                                    <div className="toolbar-row">
                                        {components.map((comp, i) => (
                                            <React.Fragment key={i}>{comp}</React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EditorToolbar;
